import FilterResponse from 'filter-wizard/models/filter-response/filter-response';
import FilterConstants from '../../../filter-constants';
import SecondOrderStage from '../../stages/second-order';
import { SpecificationParameters, FilterStage } from '../filter';

export default {
  getStages(specificationParameters: SpecificationParameters): FilterStage[] {
    const filterResponse = FilterResponse.create({ selectedResponseType: specificationParameters.filterResponseType });

    const stages = filterResponse.lowPass(
      specificationParameters.passbandAttenuation,
      specificationParameters.stopbandAttenuation,
      1,
      this._getFStop(specificationParameters.passbandFrequency, specificationParameters.stopbandFrequency),
      specificationParameters.filterResponseValue,
    );
    const qUser = specificationParameters.centerFrequency / specificationParameters.passbandFrequency;
    const narrowBandStages = stages.map((item: SecondOrderStage) => {
      let resultStage = null;
      if (item.order === 2) {
        // returns an array of two elements
        resultStage = this.narrowBandSecondOrder(item, qUser, specificationParameters.centerFrequency);
      } else {
        // returns a single element
        resultStage = this.narrowBandFirstOrder(item, qUser, specificationParameters.centerFrequency);
      }

      return resultStage;
    });

    // narrowBandStages is a mix of one element and an array of two elements.
    // to get the narrow band, it should be flattened.
    const result: FilterStage[] = [].concat(...narrowBandStages);

    return result.map((stage: FilterStage) => {
      stage.stageType = FilterConstants.filterType.bandPass;
      stage.stageFz = 0;
      return stage;
    });
  },

  getOrder(specificationParameters: SpecificationParameters): number {
    const filterResponse = FilterResponse.create({ selectedResponseType: specificationParameters.filterResponseType });
    const order = filterResponse.getOrder(
      specificationParameters.passbandAttenuation,
      specificationParameters.stopbandAttenuation,
      1,
      this._getFStop(specificationParameters.passbandFrequency, specificationParameters.stopbandFrequency),
      specificationParameters.filterResponseValue,
    );
    return order * 2;
  },

  getNumberOfStages(specificationParameters: SpecificationParameters): number {
    const order = this.getOrder(specificationParameters);
    return order / 2;
  },

  narrowBandFirstOrder(stage: SecondOrderStage, qUser: number, centerFrequency: number): SecondOrderStage {
    return SecondOrderStage.create({
      stage: stage.order,
      stageQ: qUser / stage.stageFp,
      stageFp: centerFrequency,
      gain: stage.gain,
      stageFz: 0,
    });
  },

  narrowBandSecondOrder(stage: SecondOrderStage, qUser: number, centerFrequency: number): SecondOrderStage[] {
    const a = stage.stageFp / stage.stageQ;
    const b = stage.stageFp * stage.stageFp;
    const plusTerm = (2 * qUser) / a + b / (2 * a * qUser);
    const q = Math.sqrt((qUser / a) * (plusTerm + Math.sqrt(plusTerm * plusTerm - 1)));
    const f1 = (a * q) / (2 * qUser) + 0.5 * Math.sqrt(b / (qUser * qUser) - 1 / (q * q));

    const f2 = 1 / f1;

    const fp = centerFrequency * f1;
    const fpNext = centerFrequency * f2;

    const oneByf1minusf1 = 1 / f1 - f1;
    const oneByf2minusf2 = 1 / f2 - f2;

    const gain = Math.sqrt(1 + q * q * oneByf1minusf1 * oneByf1minusf1);
    const gainNext = Math.sqrt(1 + q * q * oneByf2minusf2 * oneByf2minusf2);

    const newStage = SecondOrderStage.create({ stage: stage.order, stageQ: q, stageFp: fp, gain: gain, stageFz: 0 });
    const nextStage = SecondOrderStage.create({
      stage: stage.order,
      stageQ: q,
      stageFp: fpNext,
      gain: gainNext,
      stageFz: 0,
    });

    return [newStage, nextStage];
  },

  // returns stopband frequency
  _getFStop(passbandFrequency: number, stopbandFrequency: number): number {
    return stopbandFrequency / passbandFrequency;
  },
};
